import { catalogBadges } from "@/lib/catalog-presentation";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueries } from "@tanstack/react-query";
import { Suspense, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { Button } from "@/components/ui/button";
import { SEARCH_COPY, SEARCH_TOPICS, topicLabel, topicMatches, searchTours, withinSearchLimits } from "@/lib/search-options";
import { useCurrency } from "@/lib/currency";
import { getPreviewCalendar } from "@/lib/preview-calendar.functions";
import { nextDepartureDate } from "@/lib/availability-label";
import { productsForCity } from "@/lib/tour-search";
import { EXPLORA_DESTINATIONS } from "@/lib/explora-destinations";
import { regionCities, uniqueRegionTours } from "@/lib/home-map-regions";

import { listUnifiedCatalog } from "@/lib/catalog.functions";
import {
  matchProducts,
  sortProducts,
  SORT_OPTIONS,
  CITY_CATALOG,
  STATE_CATALOG,
  STATE_SLUGS,
  productsForState,
  type SortKey,
} from "@/lib/tour-search";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { UnifiedTourCard } from "@/components/site/UnifiedTourCard";
import { TourAutocomplete } from "@/components/site/TourAutocomplete";
import { categoryLabel } from "@/lib/category-label";

const searchSchema = z.object({
  q: z.string().optional(),
  cat: z.string().optional(),
  destination: fallback(z.string(), "").default(""),
  region: z.string().optional(),
  date: fallback(z.string(), "").default(""),
  maxPrice: fallback(z.number(), 0).optional(),
  hours: fallback(z.number(), 0).optional(),
  order: fallback(z.string(), "featured").default("featured"),
});

const catalogQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/tours/")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Tours en México — Mostrando México" },
      {
        name: "description",
        content:
          "Explora todos los tours y experiencias en México: cenotes, ruinas, gastronomía, aventura y más.",
      },
      { property: "og:title", content: "Tours en México" },
      { property: "og:description", content: "Explora tours y experiencias auténticas en México." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/tours" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(catalogQuery(context.lang)),
  component: ToursPage,
  errorComponent: CatalogErrorView,
});

function CatalogErrorView({ reset }: { reset: () => void }) {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div role="alert" className="mx-auto max-w-lg px-6 py-24 text-center">
        <p className="text-base text-foreground">{t("catalogPage.loadError")}</p>
        <Button variant="ghost"
          type="button"
          className="mt-6 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground"
          onClick={() => { router.invalidate(); reset(); }}
        >
          {t("catalogPage.retry")}
        </Button>
      </div>
      <SiteFooter />
    </div>
  );
}

function CatalogSkeleton() {
  return (
    <div className="mx-auto grid max-w-6xl gap-6 px-6 py-16 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-72 animate-pulse rounded-2xl bg-muted" />
      ))}
    </div>
  );
}

function ToursPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<CatalogSkeleton />}>
        <ToursBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function ToursBody() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data } = useSuspenseQuery(catalogQuery(lang));
  const params = Route.useSearch();
  const { q, cat } = params;
  const navigate = Route.useNavigate();
  const L = SEARCH_COPY[lang];
  const { currency, convert } = useCurrency();
  const [search, setSearch] = useState(q ?? "");
  const [selected, setSelected] = useState<string | null>(cat ?? null);
  const [sort, setSort] = useState(params.order);
  useEffect(() => { setSearch(q ?? ""); setSelected(cat ?? null); setSort(params.order); }, [q, cat, params.order]);
  const update = (next: Partial<typeof params>) => navigate({ search: prev => ({ ...prev, ...next }), replace: true });

  const tours = data.tours;

  const allCats = useMemo(() => {
    const set = new Set<string>();
    tours.forEach((p) => p.categories.forEach((c) => set.add(c)));
    return Array.from(set).slice(0, 14);
  }, [tours]);

  const candidates = useMemo(() => {
    const matched = searchTours(tours, search);
    const regionalRows = params.region ? uniqueRegionTours(regionCities(params.region, EXPLORA_DESTINATIONS), slug => productsForCity(matched, slug)) : matched;
    const destinationRows = params.destination ? productsForCity(regionalRows, params.destination) : regionalRows;
    return destinationRows.filter(p => (!selected || (SEARCH_TOPICS.some(topic => topic.id === selected) ? topicMatches(p, selected) : p.categories.includes(selected))) && withinSearchLimits(p, p.priceFrom == null ? null : convert(p.priceFrom, p.currency), params.maxPrice, params.hours));
  }, [tours, search, selected, params.destination, params.region, params.maxPrice, params.hours, convert]);
  const needsCalendar = !!params.date || sort === "soon";
  const calendars = useQueries({ queries: candidates.map(p => ({
    queryKey: ["search-calendar", p.id, lang, currency],
    queryFn: () => getPreviewCalendar({ data: { id: p.id, lang, currency } }),
    enabled: needsCalendar && p.source === "bokun", staleTime: 300_000, retry: 0,
  })) });
  const loadingDepartures = needsCalendar && calendars.some(q => q.isLoading);
  const filtered = useMemo(() => {
    const rows = candidates.map((p, i) => ({ p, slots: calendars[i]?.data?.slots }));
    const dated = params.date ? rows.filter(r => r.slots?.some(s => s.date === params.date && s.available && !s.soldOut)) : rows;
    if (sort === "soon") return dated.filter(r => r.slots && nextDepartureDate(r.slots)).sort((a, b) => (nextDepartureDate(a.slots ?? []) ?? "").localeCompare(nextDepartureDate(b.slots ?? []) ?? "")).map(r => r.p);
    if (sort === "price") return dated.map(r => r.p).sort((a,b) => (a.priceFrom == null ? Infinity : convert(a.priceFrom,a.currency)) - (b.priceFrom == null ? Infinity : convert(b.priceFrom,b.currency)));
    return sortProducts(dated.map(r => r.p), "featured");
  }, [candidates, calendars, params.date, sort, convert]);

  const featuredStates = useMemo(
    () =>
      STATE_SLUGS.map((slug) => ({
        slug,
        name: STATE_CATALOG[slug].name,
        count: productsForState(tours, slug).length,
      }))
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count),
    [tours],
  );

  const featuredCities = useMemo(
    () =>
      Object.entries(CITY_CATALOG)
        .slice(0, 8)
        .map(([slug, c]) => ({ slug, name: c.name, region: c.region })),
    [],
  );

  return (
    <>
      <div className="border-b border-border bg-surface">
        <div className="mm-container py-8 sm:py-10 lg:py-12">
          <h1 className="mm-h2 font-semibold">{t("catalogPage.title")}</h1>
          <p className="mt-2 max-w-2xl mm-lead text-muted-foreground">
            {t("catalogPage.subtitle", { count: tours.length })}
            {data.counts.custom > 0 && (
              <span className="ml-1 text-foreground/70">
                {" "}
                {t("catalogPage.curated", { count: data.counts.custom })}
              </span>
            )}
          </p>

          <div className="mt-6 flex h-14 items-center gap-3 rounded-2xl border border-border bg-background px-4 shadow-card transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <TourAutocomplete
              products={tours}
              value={search}
              onChange={setSearch}
              onSubmit={value => update({ q: value })}
              placeholder={t("catalogPage.searchPlaceholder")}
            />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="text-xs font-medium text-foreground">{L.destination}<select aria-label={L.destination} className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-2 text-sm" value={params.destination} onChange={e => update({ destination: e.target.value, region: undefined })}><option value="">{L.all}</option>{Object.entries(CITY_CATALOG).map(([slug,c]) => <option key={slug} value={slug}>{c.name}</option>)}</select></label>
            <label className="text-xs font-medium text-foreground">{L.date}<input aria-label={L.date} type="date" value={params.date} onChange={e => update({ date: e.target.value })} className="mt-1 h-11 w-full min-w-0 rounded-lg border border-border bg-background px-2 text-sm" /></label>
            <label className="text-xs font-medium text-foreground">{L.categories}<select aria-label={L.categories} value={selected ?? ""} onChange={e => update({ cat: e.target.value || undefined })} className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-2 text-sm"><option value="">{L.all}</option>{SEARCH_TOPICS.map(topic => <option key={topic.id} value={topic.id}>{topicLabel(topic,lang)}</option>)}</select></label>
            <label className="text-xs font-medium text-foreground">{L.price} ({currency})<input aria-label={L.price} type="number" min="0" value={params.maxPrice ?? ""} onChange={e => update({ maxPrice: e.target.value ? Math.max(0, Number(e.target.value)) : undefined })} className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
            <label className="text-xs font-medium text-foreground">{L.duration}<input aria-label={L.duration} type="number" min="0" value={params.hours ?? ""} onChange={e => update({ hours: e.target.value ? Math.max(0, Number(e.target.value)) : undefined })} className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
          </div>
          <div className="mt-6">
            <p className="mb-2.5 mm-eyebrow text-muted-foreground">{t("catalogPage.byState")}</p>
            <div className="flex flex-wrap gap-2">
              {featuredStates.map((s) => (
                <Link
                  key={s.slug}
                  to="/destinos/$state"
                  params={{ state: s.slug }}
                  className="inline-flex h-8 items-center rounded-full border border-primary/40 bg-primary/5 px-3.5 text-xs font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
                >
                  {s.name} <span className="opacity-70">· {s.count}</span>
                </Link>
              ))}
              <Link
                to="/destinos"
                className="inline-flex h-8 items-center rounded-full border border-border bg-background px-3.5 text-xs font-semibold text-foreground/80 transition hover:border-primary hover:text-primary"
              >
                {t("catalogPage.seeAll")}
              </Link>
            </div>
          </div>

          <div className="mt-2.5 flex flex-wrap gap-2">
            {featuredCities.map((c) => (
              <Link
                key={c.slug}
                to="/tours/$city"
                params={{ city: c.slug }}
                className="inline-flex h-8 items-center rounded-full border border-border bg-background px-3.5 text-xs font-semibold text-foreground/80 transition hover:border-primary hover:text-primary"
              >
                {c.name}
              </Link>
            ))}
          </div>

          {allCats.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip active={!selected} onClick={() => update({ cat: undefined })}>
                {t("catalogPage.allCategories")}
              </Chip>
              {allCats.map((c) => (
                <Chip key={c} active={selected === c} onClick={() => update({ cat: c })}>
                  {categoryLabel(c)}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mm-container py-8 sm:py-10 lg:py-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-muted-foreground">
            {filtered.length === 1
              ? t("catalogPage.foundOne", { count: filtered.length })
              : t("catalogPage.foundMany", { count: filtered.length })}
          </p>
          <div className="hide-scrollbar inline-flex max-w-full overflow-x-auto rounded-full bg-muted p-1 text-xs font-semibold">
            {[{ key: "featured", label: L.recommended }, { key: "price", label: L.cheapest }, { key: "soon", label: L.soon }].map((opt) => (
              <Button variant="ghost"
                key={opt.key}
                type="button"
                onClick={() => update({ order: opt.key })}
                className={`inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-full px-3.5 transition ${
                  sort === opt.key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {loadingDepartures ? <p role="status" className="py-8 text-center text-muted-foreground">{L.loading}</p> : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center sm:p-12">
            <p className="font-display text-2xl">{t("catalogPage.emptyTitle")}</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Button variant="ghost"
                type="button"
                onClick={() => {
                  setSearch("");
                  update({ q: "", cat: undefined, destination: "", region: undefined, date: "", maxPrice: undefined, hours: undefined });
                }}
                className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground"
              >
                {t("catalogPage.clearFilters")}
              </Button>
              <Link
                to="/tours"
                className="inline-flex h-11 items-center justify-center rounded-full border border-border bg-background px-5 text-sm font-semibold text-foreground/80 transition hover:border-primary hover:text-primary"
              >
                {t("catalogPage.seeAllExperiences")}
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.slice(0, 60).map((t, i) => (
              <UnifiedTourCard key={`${t.source}-${t.id}`} tour={t} badge={catalogBadges(filtered.slice(0, 60)).get(t.id)} priority={i < 2} />
            ))}
          </div>
        )}
        {filtered.length > 60 && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("catalogPage.showingLimit", { shown: 60, total: filtered.length })}
          </p>
        )}
      </div>
    </>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button variant="ghost"
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "border border-border bg-background text-foreground/80 hover:bg-accent"
      }`}
    >
      {children}
    </Button>
  );
}
